/**
 * externalLinks.js - 外部リンク設定（NFT購入URLなど）
 *
 * 【運用ルール】
 * - URLが確定したスポットから順に追記してデプロイする
 * - collectionId: そのスポットが属するNFTコレクションのID（施設単位）
 * - free:   無料版カード情報 { cardId, status }
 * - paid:   有料版カード情報 { cardId, status }
 * - status: 'available' | 'sold_out' | 'distribution_ended'
 *   - 'available': 販売・配布中
 *   - 'sold_out': 有料版が販売終了
 *   - 'distribution_ended': 無料版が配布終了
 * - 未確定スポットはエントリごと省略でOK（getNftCardInfo が null を返す）
 *
 * 【URLの構造】
 * https://nft.rural.ne.jp/#/card-details/{collectionId}/{cardId}
 *
 * 【他画面での利用】
 * getNftCardInfo(spotId, 'free')  → { url, status, isAvailable, label }
 * getNftPurchaseUrl(spotId, 'free') → URL文字列（後方互換性のため残す）
 * null が返る場合はボタンを非表示にすること。
 */

const NFT_BASE_URL = 'https://nft.rural.ne.jp/#/card-details';

/**
 * ステータス定義
 */
const NFT_STATUS = {
  AVAILABLE: 'available',              // 販売・配布中
  SOLD_OUT: 'sold_out',                // 販売終了
  DISTRIBUTION_ENDED: 'distribution_ended'  // 配布終了
};

/**
 * スポット別NFTカードID・ステータス管理
 *
 * キー: spotId（spotMeta.js と同じキー体系）
 * 値:
 *   collectionId {string} - NFTコレクションID（施設単位）
 *   free         {object|null} - 無料版カード情報
 *     └─ cardId {string} - カードID
 *     └─ status {string} - 'available' | 'sold_out' | 'distribution_ended'
 *   paid         {object|null} - 有料版カード情報
 *     └─ cardId {string} - カードID
 *     └─ status {string} - 'available' | 'sold_out'
 */
const NFT_CARD_IDS = {
  // ── 上野動物園 ────────────────────────────────────────────────
  ueno_1: { 
    collectionId: '2330', 
    paid: { cardId: '2245', status: "sold_out" } 
  },
  ueno_2: { 
    collectionId: '2330', 
    free: { cardId: '2243', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2245', status: NFT_STATUS.AVAILABLE } 
  },
  ueno_3: { 
    collectionId: '2330', 
    free: { cardId: '2237', status: "distribution_ended" }, 
    paid: { cardId: '2252', status: "sold_out" } 
  },
  ueno_4: { 
    collectionId: '2330', 
    free: { cardId: '2238', status: "distribution_ended" }, 
    paid: { cardId: '2247', status: "sold_out" } 
  },
  ueno_5: { 
    collectionId: '2330', 
    free: { cardId: '2241', status: "distribution_ended" }, 
    paid: { cardId: '2248', status: "sold_out" } 
  },
  ueno_6: { 
    collectionId: '2330', 
    free: { cardId: '2242', status: "distribution_ended" }, 
    paid: { cardId: '2249', status: "sold_out" } 
  },

  // ── 桂浜 ────────────────────────────────────────────────
  katsurahama_7: { 
    collectionId: '2331', 
    free: { cardId: '2302', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2308', status: NFT_STATUS.AVAILABLE } 
  },
  katsurahama_8: { 
    collectionId: '2331', 
    free: { cardId: '2304', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2312', status: NFT_STATUS.AVAILABLE } 
  },
  katsurahama_9: { 
    collectionId: '2331', 
    free: { cardId: '2305', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2310', status: NFT_STATUS.AVAILABLE } 
  },
  katsurahama_10: { 
    collectionId: '2331', 
    free: { cardId: '2303', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2309', status: NFT_STATUS.AVAILABLE } 
  },
  katsurahama_11: { 
    collectionId: '2331', 
    free: { cardId: '2306', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2311', status: NFT_STATUS.AVAILABLE } 
  },
  katsurahama_12: { 
    collectionId: '2331', 
    free: { cardId: '2307', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2313', status: NFT_STATUS.AVAILABLE } 
  },
  katsurahama_13: { 
    collectionId: '2331', 
    paid: { cardId: '2314', status: NFT_STATUS.AVAILABLE } 
  },

  // ── 葛西臨海水族園 ────────────────────────────────────────────
  kasairinkai_14: { 
    collectionId: '2332', 
    free: { cardId: '2330', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2330', status: NFT_STATUS.AVAILABLE } 
  },
  kasairinkai_15: { 
    collectionId: '2332', 
    free: { cardId: '2325', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2331', status: NFT_STATUS.AVAILABLE } 
  },
  kasairinkai_16: { 
    collectionId: '2332', 
    free: { cardId: '2326', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2332', status: NFT_STATUS.AVAILABLE } 
  },
  kasairinkai_17: { 
    collectionId: '2332', 
    free: { cardId: '2327', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2333', status: NFT_STATUS.AVAILABLE } 
  },
  kasairinkai_18: { 
    collectionId: '2332', 
    free: { cardId: '2328', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2334', status: NFT_STATUS.AVAILABLE } 
  },
  kasairinkai_19: { 
    collectionId: '2332', 
    free: { cardId: '2329', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2335', status: NFT_STATUS.AVAILABLE } 
  },

  // ── 日本全国ラリー（all_21〜all_35）────────────────────────────
  all_21: { 
    collectionId: '2334', 
    free: { cardId: '2367', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2382', status: NFT_STATUS.AVAILABLE } 
  },  // おびひろ動物園
  all_22: { 
    collectionId: '2335', 
    free: { cardId: '2368', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2383', status: NFT_STATUS.AVAILABLE } 
  },  // 盛岡市動物公園ZOOMO
  all_23: { 
    collectionId: '2336', 
    free: { cardId: '2369', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2384', status: NFT_STATUS.AVAILABLE } 
  },  // 八木山動物公園フジサキの杜
  all_24: { 
    collectionId: '2337', 
    free: { cardId: '2370', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2385', status: NFT_STATUS.AVAILABLE } 
  },  // アクアマリンふくしま
  all_25: { 
    collectionId: '2338', 
    free: { cardId: '2371', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2386', status: NFT_STATUS.AVAILABLE } 
  },  // 那須どうぶつ王国
  all_26: { 
    collectionId: '2339', 
    free: { cardId: '2372', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2387', status: NFT_STATUS.AVAILABLE } 
  },  // 東武動物公園
  all_27: { 
    collectionId: '2340', 
    free: { cardId: '2373', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2388', status: NFT_STATUS.AVAILABLE } 
  },  // 多摩動物公園
  all_28: { 
    collectionId: '2341', 
    free: { cardId: '2374', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2389', status: NFT_STATUS.AVAILABLE } 
  },  // 井の頭自然文化園
  all_29: { 
    collectionId: '2342', 
    free: { cardId: '2375', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2390', status: NFT_STATUS.AVAILABLE } 
  },  // 千葉市動物公園
  all_30: { 
    collectionId: '2343', 
    free: { cardId: '2376', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2391', status: NFT_STATUS.AVAILABLE } 
  },  // 野毛山動物園
  all_31: { 
    collectionId: '2344', 
    free: { cardId: '2377', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2392', status: NFT_STATUS.AVAILABLE } 
  },  // 市原ぞうの国
  all_32: { 
    collectionId: '2345', 
    free: { cardId: '2378', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2393', status: NFT_STATUS.AVAILABLE } 
  },  // 鴨川シーワールド
  all_33: { 
    collectionId: '2346', 
    free: { cardId: '2379', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2394', status: NFT_STATUS.AVAILABLE } 
  },  // サンシャイン水族館
  all_34: { 
    collectionId: '2347', 
    free: { cardId: '2380', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2395', status: NFT_STATUS.AVAILABLE } 
  },  // マクセル アクアパーク品川
  all_35: { 
    collectionId: '2348', 
    free: { cardId: '2381', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2396', status: NFT_STATUS.AVAILABLE } 
  },  // よこはま動物園ズーラシア

  // ── all ラリー西日本（新規スポット） ─────────────────────────────────
  
  all_36: { 
    collectionId: '2350', 
    free: { cardId: '2412', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2429', status: NFT_STATUS.AVAILABLE } 
  },
  all_37: { 
    collectionId: '2351', 
    free: { cardId: '2413', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2430', status: NFT_STATUS.AVAILABLE } 
  },
  all_38: { 
    collectionId: '2352', 
    free: { cardId: '2414', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2431', status: NFT_STATUS.AVAILABLE } 
  },
  all_39: { 
    collectionId: '2353', 
    free: { cardId: '2415', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2432', status: NFT_STATUS.AVAILABLE } 
  },
  all_40: { 
    collectionId: '2354', 
    free: { cardId: '2416', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2433', status: NFT_STATUS.AVAILABLE } 
  },
  all_41: { 
    collectionId: '2355', 
    free: { cardId: '2417', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2434', status: NFT_STATUS.AVAILABLE } 
  },
  all_42: { 
    collectionId: '2356', 
    free: { cardId: '2418', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2435', status: NFT_STATUS.AVAILABLE } 
  },
  all_43: { 
    collectionId: '2357', 
    free: { cardId: '2419', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2438', status: NFT_STATUS.AVAILABLE } 
  },
  all_44: { 
    collectionId: '2358', 
    free: { cardId: '2420', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2437', status: NFT_STATUS.AVAILABLE } 
  },
  all_45: { 
    collectionId: '2360', 
    free: { cardId: '2421', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2438', status: NFT_STATUS.AVAILABLE } 
  },
  all_46: { 
    collectionId: '2362', 
    free: { cardId: '2422', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2439', status: NFT_STATUS.AVAILABLE } 
  }, 
  all_47: { 
    collectionId: '2361', 
    free: { cardId: '2423', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2440', status: NFT_STATUS.AVAILABLE } 
  },
  all_48: { 
    collectionId: '2363', 
    free: { cardId: '2424', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2441', status: NFT_STATUS.AVAILABLE } 
  },
  all_49: { 
    collectionId: '2364', 
    free: { cardId: '2425', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2442', status: NFT_STATUS.AVAILABLE } 
  },
  all_50: { 
    collectionId: '2365', 
    free: { cardId: '2426', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2443', status: NFT_STATUS.AVAILABLE } 
  },
  all_51: { 
    collectionId: '2366', 
    free: { cardId: '2427', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2444', status: NFT_STATUS.AVAILABLE } 
  },
  all_52: { 
    collectionId: '2367', 
    free: { cardId: '2428', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2445', status: NFT_STATUS.AVAILABLE } 
  },

  // ── 千葉市動物公園 ────────────────────────────────────────────
  chibazoo_53: { 
    collectionId: '2342', 
    free: { cardId: '2477', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2485', status: NFT_STATUS.AVAILABLE } 
  },
  chibazoo_54: { 
    collectionId: '2342', 
    free: { cardId: '2478', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2486', status: NFT_STATUS.AVAILABLE } 
  },
  chibazoo_55: { 
    collectionId: '2342', 
    free: { cardId: '2479', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2487', status: NFT_STATUS.AVAILABLE } 
  },
  chibazoo_56: { 
    collectionId: '2342', 
    free: { cardId: '2480', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2488', status: NFT_STATUS.AVAILABLE } 
  },
  chibazoo_57: { 
    collectionId: '2342', 
    free: { cardId: '2481', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2489', status: NFT_STATUS.AVAILABLE } 
  },
  chibazoo_58: { 
    collectionId: '2342', 
    free: { cardId: '2496', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2499', status: NFT_STATUS.AVAILABLE } 
  },
  chibazoo_59: { 
    collectionId: '2342', 
    free: { cardId: '2497', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2500', status: NFT_STATUS.AVAILABLE } 
  },
  chibazoo_60: { 
    collectionId: '2342', 
    free: { cardId: '2498', status: NFT_STATUS.AVAILABLE }, 
    paid: { cardId: '2501', status: NFT_STATUS.AVAILABLE } 
  },

  // ── all ラリー（中部） ────────────────────────────────────────
  all_62: { collectionId: '2368', free: { cardId: '2523', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2543', status: NFT_STATUS.AVAILABLE } }, // 富山市ファミリーパーク
  all_63: { collectionId: '2369', free: { cardId: '2524', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2544', status: NFT_STATUS.AVAILABLE } }, // いしかわ動物園
  all_64: { collectionId: '2370', free: { cardId: '2525', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2545', status: NFT_STATUS.AVAILABLE } }, // のとじま臨海公園水族館
  all_65: { collectionId: '2371', free: { cardId: '2526', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2546', status: NFT_STATUS.AVAILABLE } }, // 鯖江市西山動物園
  all_66: { collectionId: '2372', free: { cardId: '2527', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2547', status: NFT_STATUS.AVAILABLE } }, // 飯田市立動物園
  all_67: { collectionId: '2373', free: { cardId: '2528', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2548', status: NFT_STATUS.AVAILABLE } }, // 長野市茶臼山動物園
  all_68: { collectionId: '2375', free: { cardId: '2529', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2549', status: NFT_STATUS.AVAILABLE } }, // 富士サファリパーク
  all_69: { collectionId: '2376', free: { cardId: '2530', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2550', status: NFT_STATUS.AVAILABLE } }, // 伊豆アニマルキングダム
  all_70: { collectionId: '2377', free: { cardId: '2531', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2551', status: NFT_STATUS.AVAILABLE } }, // 熱川バナナワニ園
  all_71: { collectionId: '2378', free: { cardId: '2558', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2559', status: NFT_STATUS.AVAILABLE } }, // 伊豆シャボテン動物公園
  all_72: { collectionId: '2379', free: { cardId: '2533', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2552', status: NFT_STATUS.AVAILABLE } }, // 静岡市立日本平動物園
  all_73: { collectionId: '2380', free: { cardId: '2534', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2553', status: NFT_STATUS.AVAILABLE } }, // 浜松市動物園
  all_74: { collectionId: '2381', free: { cardId: '2535', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2554', status: NFT_STATUS.AVAILABLE } }, // 名古屋市東山動植物園
  all_76: { collectionId: '2383', free: { cardId: '2537', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2556', status: NFT_STATUS.AVAILABLE } }, // 岡崎市東公園動物園
  all_77: { collectionId: '2384', free: { cardId: '2539', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2557', status: NFT_STATUS.AVAILABLE } }, // 名古屋港水族館

  // ── nonhoi ラリー ─────────────────────────────────────────────
  // TODO: GAS担当者からcollectionId・cardIdを受け取り次第、各エントリを追加してください
  nonhoi_75: { collectionId: '2382', free: { cardId: '2536', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2555', status: NFT_STATUS.AVAILABLE } }, // のんほいパーク(豊橋総合動植物公園)
  nonhoi_78: { collectionId: '2382', free: { cardId: '2572', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2583', status: NFT_STATUS.AVAILABLE } }, // のんほいパーク(園内①) マンドリルエリア
  nonhoi_79: { collectionId: '2382', free: { cardId: '2573', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2584', status: NFT_STATUS.AVAILABLE } }, // のんほいパーク(園内②) 新ゾウ舎(建設中)
  nonhoi_80: { collectionId: '2382', free: { cardId: '2574', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2585', status: NFT_STATUS.AVAILABLE } }, // のんほいパーク(園内③) 夜行性動物館
  nonhoi_81: { collectionId: '2382', free: { cardId: '2575', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2586', status: NFT_STATUS.AVAILABLE } }, // JR 二川駅
  nonhoi_82: { collectionId: '2382', free: { cardId: '2576', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2587', status: NFT_STATUS.AVAILABLE } }, // 豊橋観光案内所
  nonhoi_83: { collectionId: '2382', free: { cardId: '2577', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2588', status: NFT_STATUS.AVAILABLE } }, // JR 豊橋駅
  nonhoi_84: { collectionId: '2382', free: { cardId: '2578', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2589', status: NFT_STATUS.AVAILABLE } }, // 吉田城
  nonhoi_85: { collectionId: '2382', free: { cardId: '2579', status: NFT_STATUS.AVAILABLE }, paid: { cardId: '2590', status: NFT_STATUS.AVAILABLE } }, // 手筒花火体験パーク(豊橋市役所東館13階)
};

/**
 * NFTカード情報を取得（URL + ステータス + ラベル）
 *
 * @param {string} spotId - スポットID（例: "ueno_2"）
 * @param {'free'|'paid'} cardType - カード種別
 * @returns {object|null} - { url, status, isAvailable, label }
 *   url: 購入URL
 *   status: 'available' | 'sold_out' | 'distribution_ended'
 *   isAvailable: true ならボタン有効、false なら無効
 *   label: ボタンに表示するテキスト（状態に応じて変わる）
 *
 * @example
 * getNftCardInfo('ueno_2', 'free')
 * // → {
 * //   url: 'https://nft.rural.ne.jp/#/card-details/2330/2243',
 * //   status: 'available',
 * //   isAvailable: true,
 * //   label: '入手する'
 * // }
 *
 * getNftCardInfo('ueno_2', 'free') // status: 'distribution_ended'
 * // → {
 * //   url: '...',
 * //   status: 'distribution_ended',
 * //   isAvailable: false,
 * //   label: '配布終了'
 * // }
 */
export const getNftCardInfo = (spotId, cardType) => {
  const ids = NFT_CARD_IDS[spotId];
  if (!ids) return null;

  const card = ids[cardType];
  if (!card) return null;

  const url = `${NFT_BASE_URL}/${ids.collectionId}/${card.cardId}`;
  const isAvailable = card.status === NFT_STATUS.AVAILABLE;

  // ステータスに応じたラベルを生成
  let label = '';
  if (card.status === NFT_STATUS.AVAILABLE) {
    label = cardType === 'free' ? '入手する' : '購入する';
  } else if (card.status === NFT_STATUS.DISTRIBUTION_ENDED) {
    label = '配布終了';
  } else if (card.status === NFT_STATUS.SOLD_OUT) {
    label = '販売終了';
  }

  return {
    url,
    status: card.status,
    isAvailable,
    label
  };
};

/**
 * NFT購入URLを取得（後方互換性のため残す）
 *
 * @param {string} spotId - スポットID
 * @param {'free'|'paid'} cardType - カード種別
 * @returns {string|null} - 購入URL。未設定の場合は null
 *
 * @deprecated getNftCardInfo() を使用してください
 */
export const getNftPurchaseUrl = (spotId, cardType) => {
  const info = getNftCardInfo(spotId, cardType);
  return info?.url ?? null;
};
